import { useModal } from '@billy/design-system'
import noop from 'lodash/noop'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { Contact } from '@features/contacts/types/contact'
import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { useUpdateCustomerInformation } from '@features/invoices/hooks/useUpdateCustomerInformation'

import { ModalId } from '../../../../../../../enums/modalId'
import { QueryKeys } from '../../../../../../../enums/queryKeys'
import { useForm, UseFormReturn } from '../../../../../../../hooks'
import { usePredefinedCustomerValues } from '../../../hooks/missingComplianceDetails/usePredefinedCustomerValues'
import { useUpdateReceiverDependentValues } from '../../../hooks/useUpdateReceiverDependentValues'
import { getCustomerFormDefaultValues } from '../../../utils/missingComplianceDetails/getCustomerFormDefaultValues'
import { MissingCustomerDetailsFormSchema } from '../types/missingCustomerDetailsFormSchema'
import { getCustomerFormValidationSchemaByComplianceMode } from '../utils/getCustomerFormValidationSchemaByComplianceMode'

interface ContextState {
  isLoading: boolean
  isSubmitting: boolean
  missingCustomerDetailsForm: UseFormReturn<MissingCustomerDetailsFormSchema>
  onConfirm: (() => void) | null
  submitForm: () => void
  validate: () => Promise<boolean>
}

const MissingCustomerDetailsFormContext = createContext<ContextState | undefined>(undefined)

interface MissingCustomerDetailsFormProviderProps {
  children?: ReactNode
  onConfirm: (() => void) | null
}

export const MissingCustomerDetailsFormContextProvider = ({
  children,
  onConfirm,
}: MissingCustomerDetailsFormProviderProps): ReactElement => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { predefinedCustomerValues } = usePredefinedCustomerValues()
  const { handleReceiverUpdate } = useUpdateReceiverDependentValues()
  const { close: closeMissingComplianceDetailsModal } = useModal(ModalId.InvoiceEditorMissingComplianceDetailsModal)
  const { invoiceComplianceMode } = useInvoiceComplianceMode()

  const missingCustomerDetailsForm = useForm<MissingCustomerDetailsFormSchema>({
    defaultValues: getCustomerFormDefaultValues(),
    validationSchema: getCustomerFormValidationSchemaByComplianceMode({ complianceMode: invoiceComplianceMode, t }),
  })

  const { handleSubmit, reset: resetForm, trigger } = missingCustomerDetailsForm

  const handleUpdateCustomerOnSubmit = useCallback(
    async (customer: Contact) => {
      queryClient.invalidateQueries([QueryKeys.ContactsAndContactPersons])
      handleReceiverUpdate(customer)
      onConfirm?.()
      closeMissingComplianceDetailsModal()
    },
    [closeMissingComplianceDetailsModal, onConfirm, handleReceiverUpdate, queryClient],
  )

  const { submit, isLoading, isSubmitting } = useUpdateCustomerInformation({ onSubmit: handleUpdateCustomerOnSubmit })

  useEffect(() => {
    if (predefinedCustomerValues) {
      const updatedMissingCustomerDetailsFormValues = getCustomerFormDefaultValues(predefinedCustomerValues)
      resetForm(updatedMissingCustomerDetailsFormValues)
    }
  }, [predefinedCustomerValues, resetForm])

  // we're using a fake submit for validation due to how useForm's mode param works
  const handleValidation = useCallback(async () => {
    await handleSubmit(noop)()

    return trigger()
  }, [handleSubmit, trigger])

  const submitForm = useCallback(() => {
    handleSubmit(submit)()
  }, [handleSubmit, submit])

  const contextValue = useMemo(
    () => ({
      isLoading,
      isSubmitting,
      missingCustomerDetailsForm,
      onConfirm,
      submitForm,
      validate: handleValidation,
    }),
    [handleValidation, isLoading, isSubmitting, missingCustomerDetailsForm, onConfirm, submitForm],
  )

  return (
    <MissingCustomerDetailsFormContext.Provider value={contextValue}>
      {children}
    </MissingCustomerDetailsFormContext.Provider>
  )
}

export const useMissingCustomerDetailsForm = () => {
  const context = useContext(MissingCustomerDetailsFormContext)

  if (!context) {
    throw new Error('MissingCustomerDetailsFormContextProvider is missing in the module!')
  }

  return context
}
